import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * App-level ErrorBoundary. Catches render-time exceptions from any child route
 * (e.g. rules-of-hooks violations, accidental null-deref) and shows a friendly
 * fallback instead of leaving the screen blank with only the body bg color
 * showing through. Without this, an uncaught render error unmounts the entire
 * tree and the user sees a black screen with no way to recover.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (typeof window !== 'undefined' && (window as any).console) {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary] caught render error:', error, info?.componentStack);
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div className="hv-theme min-h-screen flex flex-col items-center justify-center bg-[var(--bg)] text-[var(--tx)] px-6">
          <div className="max-w-[480px] text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 mb-4 rounded-full bg-[var(--rd-d)] text-[var(--rd-t)]">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
                <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h1 className="hv-serif text-[28px] font-bold mb-2">Something went wrong on this page.</h1>
            <p className="text-[15px] text-[var(--tx2)] mb-6">
              The page failed to load. You can head back to your dashboard or try again — your work isn't lost.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => { this.reset(); window.location.reload(); }}
                className="px-5 py-2.5 rounded-xl bg-[var(--warm)] text-[var(--warm-on)] font-semibold hover:bg-[var(--warm-hover)]"
              >
                Reload
              </button>
              <button
                onClick={() => { this.reset(); window.location.href = '/dashboard'; }}
                className="px-5 py-2.5 rounded-xl bg-[var(--sf2)] text-[var(--tx)] font-semibold border border-[var(--bdr)] hover:border-[var(--warm)]"
              >
                Back to dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
